import React, { RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { displayName, initials, isLongStay, nextStopIndex, toPercent } from './pickupTrackingLogic';
import { STOP_STATUS_COLOR, vehicleThemeOf } from './pickupTrackingTheme';
import type { TrackingDriver } from './pickupTrackingTypes';
import styles from './PickupDriverTrackingPage.module.css';

const NEXT_ROW_OFFSET = 70;

const STOP_CLASS = {
    ok: styles.stopOk,
    fail: styles.stopFail,
    cur: '',
    wait: '',
} as const;

interface TrackingDriverPopupProps {
    driver: TrackingDriver;
    thresholdMin: number;
    /** 地图面板的 rAF 循环按选中车辆的屏幕位置摆这个节点（先 visibility:hidden，摆好再显示） */
    popupRef: RefObject<HTMLDivElement>;
    onClose: () => void;
    onMessage: () => void;
    /** 地图上没有这辆车时「地图定位」置灰（同派送大屏） */
    canLocate: boolean;
    onLocate: () => void;
    /** 轨迹查询中，「当日轨迹」置灰防重复点（同派送大屏） */
    replayLoading: boolean;
    onReplay: () => void;
}

export const TrackingDriverPopup: React.FC<TrackingDriverPopupProps> = ({
    driver: d,
    thresholdMin,
    popupRef,
    onClose,
    onMessage,
    canLocate,
    onLocate,
    replayLoading,
    onReplay,
}) => {
    const { t } = useTranslation();
    const stopsRef = useRef<HTMLDivElement>(null);
    const color = vehicleThemeOf(d.vehicleType).color;
    const name = displayName(d);
    const handled = d.ok + d.fail;
    const nextIdx = nextStopIndex(d);
    const okPct = toPercent(d.ok, d.total);
    const failPct = toPercent(d.fail, d.total);

    // 打开 / 换司机时把「下一站」滚到可视区（原型：offsetTop - 70）
    useEffect(() => {
        const box = stopsRef.current;
        if (!box || nextIdx < 0) return;
        const row = box.children[nextIdx] as HTMLElement | undefined;
        if (row) box.scrollTop = Math.max(0, row.offsetTop - NEXT_ROW_OFFSET);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [d.key]);

    return (
        <div
            ref={popupRef}
            className={styles.pop}
            data-driver-id={d.key}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label={t('pickup.driverTracking.close') || ''}
                title={t('pickup.driverTracking.close') || ''}
            >
                ✕
            </button>
            <div className={styles.popHead}>
                <div className={`${styles.av} ${styles.popAv}`} style={{ color }}>
                    {initials(d.name === d.id ? null : d.name, d.id)}
                </div>
                <div>
                    <b className={styles.popName}>{name}</b>
                    <div className={styles.popMeta}>
                        {t('pickup.driverTracking.popMeta', {
                            // 产品看不懂账号 id，改放手机号；账号没维护手机号才回落 id
                            id: d.phone ?? d.id,
                            start: d.startTime ?? '—',
                            handover: d.handoverTime ?? t('pickup.driverTracking.notHanded'),
                        })}
                    </div>
                </div>
                {d.vehicleType && (
                    <span className={styles.popVt} style={{ color }}>
                        {t(`pickup.driverTracking.vehicle.${d.vehicleType}`)}
                    </span>
                )}
            </div>
            {d.anomalies.length > 0 && (
                <div className={styles.popAlert}>
                    <span className={styles.popAlertTitle}>{t('pickup.driverTracking.alertTitle')}</span>
                    {d.anomalies.map((a) => (
                        <span key={a} className={styles.atag}>
                            {t(`pickup.driverTracking.anomaly.${a}`)}
                        </span>
                    ))}
                </div>
            )}
            <div className={styles.popStats}>
                <div className={styles.popStat}>
                    <b className={styles.num} style={{ color: '#7ff0ff' }}>
                        {d.total}
                    </b>
                    <span>{t('pickup.driverTracking.pTotal')}</span>
                </div>
                <div className={styles.popStat}>
                    <b className={styles.num} style={{ color: '#34d399' }}>
                        {d.ok}
                    </b>
                    <span>{t('pickup.driverTracking.pOk')}</span>
                </div>
                <div className={styles.popStat}>
                    <b className={styles.num} style={{ color: '#f87171' }}>
                        {d.fail}
                    </b>
                    <span>{t('pickup.driverTracking.pFail')}</span>
                </div>
            </div>
            <div className={styles.popBody}>
                <div className={styles.popProg}>
                    <span>{t('pickup.driverTracking.progress')}</span>
                    <span>
                        {t('pickup.driverTracking.progressText', {
                            handled,
                            total: d.total,
                            rate: okPct,
                        })}
                    </span>
                </div>
                <div className={styles.pbar}>
                    <i style={{ width: `${okPct}%` }} />
                    <s style={{ width: `${failPct}%` }} />
                </div>
                <div className={styles.plg}>
                    <span>
                        <i style={{ background: '#34d399' }} />
                        {t('pickup.driverTracking.sumOk')} {d.ok}
                    </span>
                    <span>
                        <i style={{ background: '#f87171' }} />
                        {t('pickup.driverTracking.sumFail')} {d.fail}
                    </span>
                    <span>
                        <i style={{ background: 'rgba(56,189,248,.3)' }} />
                        {t('pickup.driverTracking.sumWait')} {Math.max(0, d.total - handled)}
                    </span>
                </div>
                <div style={{ marginTop: 14 }}>
                    <div className={styles.rtt}>{t('pickup.driverTracking.routeTitle', { total: d.total })}</div>
                    <div className={styles.sthead}>
                        <span />
                        <span>{t('pickup.driverTracking.colSite')}</span>
                        <span>{t('pickup.driverTracking.colStay')}</span>
                        <span>{t('pickup.driverTracking.colTime')}</span>
                        <span style={{ textAlign: 'right' }}>{t('pickup.driverTracking.colStatus')}</span>
                    </div>
                    <div className={styles.stops} ref={stopsRef}>
                        {d.stops.map((s, i) => {
                            const long = isLongStay(s, thresholdMin);
                            return (
                                <div
                                    key={`${s.siteId}-${i}`}
                                    className={`${styles.stop} ${STOP_CLASS[s.status]} ${i === nextIdx ? styles.stopNext : ''}`}
                                >
                                    <span className={styles.stopDot} />
                                    <span className={styles.adr}>
                                        <b>
                                            {s.siteName}
                                            {i === nextIdx && (
                                                <span className={styles.nexttag}>
                                                    {t('pickup.driverTracking.nextStop')}
                                                </span>
                                            )}
                                        </b>
                                        <em>{s.address}</em>
                                    </span>
                                    <span className={`${styles.stay} ${long ? styles.stayLong : ''}`}>
                                        {s.status === 'wait' || s.stayMin === null
                                            ? '—'
                                            : t('pickup.driverTracking.stayMinutes', { min: s.stayMin })}
                                    </span>
                                    <span className={styles.tm}>{s.operatedAt ?? '—'}</span>
                                    <span className={styles.sst} style={{ color: STOP_STATUS_COLOR[s.status] }}>
                                        {t(`pickup.driverTracking.status.${s.status}`)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.pacts}>
                    <button type="button" className={styles.pact} onClick={onMessage}>
                        {t('pickup.driverTracking.msg')}
                    </button>
                    <button type="button" className={styles.pact} disabled={!canLocate} onClick={onLocate}>
                        {t('pickup.driverTracking.focus')}
                    </button>
                    <button
                        type="button"
                        className={`${styles.pact} ${styles.pactPrimary}`}
                        disabled={replayLoading}
                        onClick={onReplay}
                    >
                        {t('pickup.driverTracking.replay')}
                    </button>
                </div>
            </div>
        </div>
    );
};
